import { z } from 'zod';
// ---------------------------------------------------------------------------
// Reusable enum schemas
// ---------------------------------------------------------------------------
const personTypeSchema = z.enum(['shelter', 'outreach', 'hotline', 'child'], {
    required_error: 'Person type is required',
    invalid_type_error: 'Person type must be shelter, outreach, hotline, or child',
});
const personStatusSchema = z.enum(['active', 'inactive', 'departed', 'transferred', 'deceased'], { invalid_type_error: 'Invalid person status' });
const genderSchema = z.enum([
    'female', 'male', 'transgender_female', 'transgender_male',
    'non_binary', 'other', 'unknown', 'declined',
]);
const ethnicitySchema = z.enum([
    'hispanic_latino', 'not_hispanic_latino', 'unknown', 'declined',
]);
const raceSchema = z.enum([
    'american_indian_alaska_native', 'asian', 'black_african_american',
    'native_hawaiian_pacific_islander', 'white', 'multiracial',
    'other', 'unknown', 'declined',
]);
const abuseTypeSchema = z.enum([
    'physical', 'emotional', 'sexual', 'economic',
    'stalking', 'trafficking', 'other',
]);
const relationshipSchema = z.enum([
    'spouse', 'ex_spouse', 'partner', 'ex_partner', 'parent',
    'child', 'sibling', 'other_family', 'caregiver', 'other', 'unknown',
]);
const isoDateString = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}/, 'Date must be in ISO-8601 format (YYYY-MM-DD)');
// ---------------------------------------------------------------------------
// Create Person (intake)
// ---------------------------------------------------------------------------
/**
 * Validates the full intake form. Conditional requirements:
 * - `child` type requires `parentPersonId`
 * - `shelter` type requires `bedId` unless the agency auto-assigns
 */
export const createPersonSchema = z
    .object({
    personType: personTypeSchema,
    firstName: z
        .string({ required_error: 'First name is required' })
        .min(1, 'First name is required')
        .max(100, 'First name must be 100 characters or fewer')
        .trim(),
    lastName: z
        .string({ required_error: 'Last name is required' })
        .min(1, 'Last name is required')
        .max(100, 'Last name must be 100 characters or fewer')
        .trim(),
    middleName: z.string().max(100).trim().optional(),
    preferredName: z.string().max(100).trim().optional(),
    dateOfBirth: isoDateString.optional(),
    ssn: z
        .string()
        .regex(/^\d{3}-?\d{2}-?\d{4}$/, 'SSN must be in the format 123-45-6789')
        .optional(),
    gender: genderSchema.optional(),
    ethnicity: ethnicitySchema.optional(),
    race: raceSchema.optional(),
    primaryLanguage: z.string().max(50).optional(),
    isInterpreterNeeded: z.boolean().optional(),
    phone: z.string().max(20).optional(),
    email: z.string().email('Invalid email address').max(255).optional(),
    address: z.string().max(255).optional(),
    city: z.string().max(100).optional(),
    state: z.string().max(2, 'Use 2-letter state code').optional(),
    zipCode: z
        .string()
        .regex(/^\d{5}(-\d{4})?$/, 'ZIP code must be 5 or 9 digits')
        .optional(),
    county: z.string().max(100).optional(),
    intakeDate: isoDateString.describe('The date of intake'),
    referralSource: z.string().max(255).optional(),
    presentingSituation: z.string().max(5000).optional(),
    abuseTypes: z.array(abuseTypeSchema).optional(),
    relationshipToAbuser: relationshipSchema.optional(),
    isAbuseCurrent: z.boolean().optional(),
    abuseStartDate: isoDateString.optional(),
    hasProtectionOrder: z.boolean().optional(),
    protectionOrderExpiration: isoDateString.optional(),
    safetyPlanCompleted: z.boolean().optional(),
    dangerAssessmentScore: z.number().min(0).max(100).optional(),
    isHighDanger: z.boolean().optional(),
    hasChildren: z.boolean().optional(),
    numberOfChildren: z.number().int().min(0).max(50).optional(),
    isPregnant: z.boolean().optional(),
    hasDisability: z.boolean().optional(),
    disabilityDetails: z.string().max(1000).optional(),
    isVeteran: z.boolean().optional(),
    employmentStatus: z.string().max(100).optional(),
    monthlyIncome: z.number().min(0).optional(),
    educationLevel: z.string().max(100).optional(),
    isHomeless: z.boolean().optional(),
    mentalHealthDiagnosis: z.string().max(1000).optional(),
    isSubstanceAbuse: z.boolean().optional(),
    substanceAbuseDetails: z.string().max(1000).optional(),
    isSuicidal: z.boolean().optional(),
    suicidalDetails: z.string().max(1000).optional(),
    medications: z.string().max(2000).optional(),
    photoUrl: z.string().url('Invalid photo URL').optional(),
    bedId: z.string().uuid('Invalid bed ID').optional(),
    expectedDepartureDate: isoDateString.optional(),
    parentPersonId: z.string().uuid('Invalid parent person ID').optional(),
    schoolName: z.string().max(200).optional(),
    gradeLevel: z.string().max(20).optional(),
    pediatrician: z.string().max(200).optional(),
    custodyStatus: z.string().max(200).optional(),
    siteId: z.string().uuid('Invalid site ID').optional(),
    funderId: z.string().uuid('Invalid funder ID').optional(),
    programId: z.string().uuid('Invalid program ID').optional(),
})
    .refine((data) => {
    if (data.personType === 'child' && !data.parentPersonId) {
        return false;
    }
    return true;
}, {
    message: 'Parent person ID is required for child intakes',
    path: ['parentPersonId'],
});
// ---------------------------------------------------------------------------
// Update Person
// ---------------------------------------------------------------------------
/** Partial person update — personType cannot be changed (use transfer). */
export const updatePersonSchema = createPersonSchema
    .innerType()
    .omit({ personType: true })
    .partial();
// ---------------------------------------------------------------------------
// Search
// ---------------------------------------------------------------------------
export const searchPersonSchema = z.object({
    query: z.string().max(200).optional(),
    personType: personTypeSchema.optional(),
    status: personStatusSchema.optional(),
    agencyId: z.string().uuid('Invalid agency ID').optional(),
    siteId: z.string().uuid('Invalid site ID').optional(),
    dateFrom: isoDateString.optional(),
    dateTo: isoDateString.optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
    sortBy: z.string().max(50).optional(),
    sortOrder: z.enum(['asc', 'desc']).default('desc'),
});
// ---------------------------------------------------------------------------
// Transfer
// ---------------------------------------------------------------------------
export const transferPersonSchema = z.object({
    personId: z.string().uuid('Invalid person ID'),
    fromType: personTypeSchema,
    toType: personTypeSchema,
    fromSiteId: z.string().uuid('Invalid source site ID').optional(),
    toSiteId: z.string().uuid('Invalid destination site ID').optional(),
    transferDate: isoDateString,
    reason: z.string().max(500).optional(),
    notes: z.string().max(2000).optional(),
}).refine((data) => data.fromType !== data.toType || data.fromSiteId !== data.toSiteId, { message: 'Transfer must change the person type or site' });
// ---------------------------------------------------------------------------
// Case Notes
// ---------------------------------------------------------------------------
export const createCaseNoteSchema = z.object({
    personId: z.string().uuid('Invalid person ID'),
    noteDate: isoDateString,
    category: z.string().max(100).optional(),
    subject: z.string().max(200).optional(),
    body: z
        .string({ required_error: 'Note body is required' })
        .min(1, 'Note body cannot be empty')
        .max(50000, 'Note body must be 50,000 characters or fewer'),
    isConfidential: z.boolean().default(false),
});
// ---------------------------------------------------------------------------
// Activities
// ---------------------------------------------------------------------------
export const createActivitySchema = z.object({
    personId: z.string().uuid('Invalid person ID'),
    serviceTypeId: z.string().uuid('Invalid service type ID'),
    activityDate: isoDateString,
    durationMinutes: z.number().int().min(0).max(1440).optional(),
    units: z.number().min(0).optional(),
    siteId: z.string().uuid('Invalid site ID').optional(),
    funderId: z.string().uuid('Invalid funder ID').optional(),
    programId: z.string().uuid('Invalid program ID').optional(),
    notes: z.string().max(5000).optional(),
});
//# sourceMappingURL=client.js.map